/**
 * Blog listing — AJAX tag filters + pagination (pages or load more).
 */

if (!customElements.get('blog-listing')) {
  class BlogListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('popstate', this.onPopState);
      this.abortController?.abort();
    }

    onClick(event) {
      const loadMore = event.target.closest('[data-blog-load-more]');
      if (loadMore && this.contains(loadMore)) {
        event.preventDefault();
        const url = loadMore.dataset.url;
        if (url) this.loadMore(url, loadMore);
        return;
      }

      const link = event.target.closest('[data-blog-ajax], a[data-render-section-url]');
      if (!link || !this.contains(link)) return;

      const url = link.getAttribute('href') || link.dataset.renderSectionUrl;
      if (!url || url === '#') return;

      event.preventDefault();
      this.load(url, { push: true });
    }

    onPopState() {
      this.load(window.location.href, { push: false });
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      const loader = this.querySelector('[data-blog-loading]');
      if (loader) loader.hidden = !isLoading;

      const results = this.querySelector('[data-blog-results]');
      if (results) results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    async fetchSection(url) {
      if (!this.sectionId) return null;

      this.abortController?.abort();
      this.abortController = new AbortController();

      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(`${requestUrl.pathname}${requestUrl.search}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Blog section fetch failed (${response.status})`);

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      return doc.querySelector('blog-listing') || doc.body;
    }

    async load(url, { push = true } = {}) {
      this.setLoading(true);

      try {
        const next = await this.fetchSection(url);
        if (!next) return;

        const nextFilters = next.querySelector('[data-blog-filters]');
        const filters = this.querySelector('[data-blog-filters]');
        if (filters && nextFilters) filters.innerHTML = nextFilters.innerHTML;

        const nextResults = next.querySelector('[data-blog-results]');
        const results = this.querySelector('[data-blog-results]');
        if (results && nextResults) results.innerHTML = nextResults.innerHTML;

        if (push) {
          const clean = new URL(url, window.location.origin);
          clean.searchParams.delete('section_id');
          history.pushState({ blogAjax: true }, '', `${clean.pathname}${clean.search}`);
        }

        const scrollTarget = this.querySelector('[data-blog-filters]') || results;
        scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          window.location.href = url;
        }
      } finally {
        this.setLoading(false);
      }
    }

    async loadMore(url, button) {
      if (!url || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      this.classList.add('is-loading-more');

      try {
        const next = await this.fetchSection(url);
        if (!next) return;

        const articles = this.querySelector('[data-blog-articles]');
        const nextArticles = next.querySelectorAll('[data-blog-article]');
        nextArticles.forEach((article) => articles?.appendChild(article));

        const nextButton = next.querySelector('[data-blog-load-more]');
        const pagination = this.querySelector('[data-blog-pagination]');

        if (nextButton?.dataset.url && pagination) {
          button.dataset.url = nextButton.dataset.url;
          button.disabled = false;
          button.classList.remove('is-loading');
        } else if (pagination) {
          pagination.remove();
        }

        const clean = new URL(url, window.location.origin);
        clean.searchParams.delete('section_id');
        history.pushState({ blogAjax: true }, '', `${clean.pathname}${clean.search}`);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(error);
          window.location.href = url;
        }
      } finally {
        this.classList.remove('is-loading-more');
        if (button.isConnected) {
          button.classList.remove('is-loading');
          if (!button.disabled) button.disabled = false;
        }
      }
    }
  }

  customElements.define('blog-listing', BlogListing);
}
